import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { 
  Users, Mail, Send, Check, X, Search, Filter, 
  CreditCard, Package, ArrowLeft, ChevronRight, Star,
  Briefcase, Building2, CheckCircle, AlertCircle, Loader2,
  MessageSquare, Calendar, FileText, ExternalLink, Bold, Italic, List, Link as LinkIcon,
  Download, RefreshCw, BarChart3
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { load } from '@cashfreepayments/cashfree-js';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  pending: 'bg-gray-100 text-gray-700',
  contacted: 'bg-blue-100 text-blue-700',
  interviewed: 'bg-purple-100 text-purple-700',
  offered: 'bg-amber-100 text-amber-700',
  hired: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700'
};

// Rich text editor modules configuration
const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    ['link'],
    ['clean']
  ],
};

const quillFormats = [
  'bold', 'italic', 'underline',
  'list', 'bullet',
  'link'
];

const ShortlistedCandidatesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [emailCredits, setEmailCredits] = useState({ available_credits: 0, total_purchased: 0, total_used: 0 });
  const [emailPackages, setEmailPackages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterChallenge, setFilterChallenge] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [jobRole, setJobRole] = useState('');
  const [interviewInstructions, setInterviewInstructions] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Purchase modal state
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  
  // Bulk status modal state
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('contacted');
  const [bulkUpdating, setBulkUpdating] = useState(false);
  
  const token = localStorage.getItem('company_token');

  useEffect(() => {
    if (!token) {
      navigate('/company/auth');
      return;
    }
    
    fetchData();
    
    // Handle payment callback
    const orderId = searchParams.get('order_id');
    if (orderId) {
      verifyPayment(orderId);
    }
  }, [token, navigate, searchParams]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [candidatesRes, creditsRes, packagesRes] = await Promise.all([
        axios.get(`${API}/api/company/shortlisted-candidates`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/company/email-credits`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/public/email-pricing`)
      ]);
      
      setCandidates(candidatesRes.data.shortlisted_candidates || []);
      setEmailCredits(creditsRes.data);
      setEmailPackages(packagesRes.data.packages || []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async (orderId) => {
    try {
      const res = await axios.post(`${API}/api/company/email-credits/verify`, 
        { order_id: orderId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (res.data.verified) {
        toast.success(`${res.data.credits_added} email credits added!`);
        fetchData();
      }
    } catch (err) {
      console.error('Payment verification failed');
    }
    
    // Clear URL params
    window.history.replaceState({}, '', '/company/shortlisted');
  };

  const handleSelectCandidate = (candidateId) => {
    setSelectedCandidates(prev => {
      if (prev.includes(candidateId)) {
        return prev.filter(id => id !== candidateId);
      }
      return [...prev, candidateId];
    });
  };

  const handleSelectAll = () => {
    const filteredIds = filteredCandidates.map(c => c.id);
    if (selectedCandidates.length === filteredIds.length) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates(filteredIds);
    }
  };

  const handlePurchaseCredits = async (packageId) => {
    setPurchaseLoading(true);
    try {
      const res = await axios.post(`${API}/api/company/email-credits/purchase`,
        { package_id: packageId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (res.data.payment_session_id) {
        const cashfree = await load({ mode: "production" });
        await cashfree.checkout({
          paymentSessionId: res.data.payment_session_id,
          redirectTarget: "_self"
        });
      } else {
        // In development/preview mode without Cashfree credentials, simulate success
        toast.info('Payment gateway not configured - simulating purchase for testing');
        // Try to verify anyway (will fail but handle gracefully)
      }
    } catch (err) {
      toast.error('Failed to initiate purchase');
    } finally {
      setPurchaseLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject || !emailMessage) {
      toast.error('Subject and message are required');
      return;
    }
    
    if (selectedCandidates.length === 0) {
      toast.error('No candidates selected');
      return;
    }
    
    if (emailCredits.available_credits < selectedCandidates.length) {
      toast.error(`Insufficient credits. You need ${selectedCandidates.length} but have ${emailCredits.available_credits}`);
      setShowEmailModal(false);
      setShowPurchaseModal(true);
      return;
    }
    
    setSendingEmail(true);
    try {
      const res = await axios.post(`${API}/api/company/bulk-email/send`, {
        recipient_ids: selectedCandidates,
        subject: emailSubject,
        message: emailMessage,
        job_role: jobRole,
        interview_instructions: interviewInstructions
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${res.data.sent} emails sent successfully!`);
      setShowEmailModal(false);
      setSelectedCandidates([]);
      setEmailSubject('');
      setEmailMessage('');
      setJobRole('');
      setInterviewInstructions('');
      fetchData(); // Refresh credits
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSendingEmail(false);
    }
  };

  const updateCandidateStatus = async (participantId, status) => {
    try {
      await axios.put(`${API}/api/company/shortlisted-candidates/${participantId}/notes`, {
        hiring_status: status
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCandidates(prev => prev.map(c => 
        c.id === participantId ? { ...c, hiring_status: status } : c
      ));
      toast.success('Status updated');
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // Bulk status update
  const handleBulkStatusUpdate = async () => {
    if (selectedCandidates.length === 0) {
      toast.error('No candidates selected');
      return;
    }
    
    setBulkUpdating(true);
    try {
      const res = await axios.put(`${API}/api/company/shortlisted-candidates/bulk-status`, {
        participant_ids: selectedCandidates,
        status: bulkStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Updated ${res.data.updated} candidates to '${bulkStatus}'`);
      setShowBulkStatusModal(false);
      setSelectedCandidates([]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setBulkUpdating(false);
    }
  };

  // Export to CSV
  const handleExportCSV = async () => {
    try {
      const res = await axios.get(`${API}/api/company/shortlisted-candidates/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.csv_data.length === 0) {
        toast.info('No data to export');
        return;
      }
      
      // Convert to CSV
      const headers = Object.keys(res.data.csv_data[0]);
      const csvContent = [
        headers.join(','),
        ...res.data.csv_data.map(row => 
          headers.map(h => `"${(row[h] || '').toString().replace(/"/g, '""')}"`).join(',')
        )
      ].join('\n');
      
      // Download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `shortlisted_candidates_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      
      toast.success(`Exported ${res.data.total} candidates`);
    } catch (err) {
      toast.error('Failed to export data');
    }
  };

  // Get unique challenges for filter
  const challenges = [...new Set(candidates.map(c => c.challenge_title))];
  
  // Filter candidates
  const filteredCandidates = candidates.filter(c => {
    const matchesSearch = !searchTerm || 
      c.student_details?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.student_details?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.student_details?.college?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesChallenge = filterChallenge === 'all' || c.challenge_title === filterChallenge;
    const matchesStatus = filterStatus === 'all' || c.hiring_status === filterStatus;
    
    return matchesSearch && matchesChallenge && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/company/dashboard" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <ArrowLeft size={20} />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Star className="text-amber-500" size={24} />
                  Shortlisted Candidates
                </h1>
                <p className="text-sm text-gray-500">{candidates.length} candidates shortlisted</p>
              </div>
            </div>
            
            {/* Email Credits Badge */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-purple-50 rounded-xl border border-purple-200">
                <Mail className="text-purple-600" size={18} />
                <span className="font-semibold text-purple-600">{emailCredits.available_credits}</span>
                <span className="text-purple-600 text-sm">Email Credits</span>
              </div>
              <button
                onClick={() => setShowPurchaseModal(true)}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium hover:shadow-lg transition-all flex items-center gap-2"
                data-testid="buy-credits-btn"
              >
                <Package size={18} />
                Buy Credits
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters & Actions Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, email, college..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  data-testid="search-input"
                />
              </div>
            </div>
            
            {/* Challenge Filter */}
            <select
              value={filterChallenge}
              onChange={(e) => setFilterChallenge(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              data-testid="filter-challenge"
            >
              <option value="all">All Challenges</option>
              {challenges.map(ch => (
                <option key={ch} value={ch}>{ch}</option>
              ))}
            </select>
            
            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              data-testid="filter-status"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="contacted">Contacted</option>
              <option value="interviewed">Interviewed</option>
              <option value="offered">Offered</option>
              <option value="hired">Hired</option>
              <option value="rejected">Rejected</option>
            </select>
            
            {/* Bulk Actions */}
            {selectedCandidates.length > 0 && (
              <>
                <button
                  onClick={() => setShowEmailModal(true)}
                  className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-medium hover:shadow-lg transition-all flex items-center gap-2"
                  data-testid="send-bulk-email-btn"
                >
                  <Send size={18} />
                  Email ({selectedCandidates.length})
                </button>
                <button
                  onClick={() => setShowBulkStatusModal(true)}
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-medium hover:shadow-lg transition-all flex items-center gap-2"
                  data-testid="bulk-status-btn"
                >
                  <RefreshCw size={18} />
                  Update Status ({selectedCandidates.length})
                </button>
              </>
            )}
            
            {/* Export & Insights */}
            <button
              onClick={handleExportCSV}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all flex items-center gap-2"
              data-testid="export-csv-btn"
            >
              <Download size={18} />
              Export CSV
            </button>
            <Link
              to="/company/interview-insights"
              className="px-4 py-2 border border-purple-300 text-purple-700 rounded-lg font-medium hover:bg-purple-50 transition-all flex items-center gap-2"
              data-testid="insights-btn"
            >
              <BarChart3 size={18} />
              Insights
            </Link>
          </div>
        </div>

        {/* Candidates Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedCandidates.length === filteredCandidates.length && filteredCandidates.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Candidate</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Challenge</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Score</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Shortlisted</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredCandidates.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-12 text-center text-gray-500">
                      <Users size={48} className="mx-auto mb-3 opacity-30" />
                      <p>No shortlisted candidates found</p>
                      <p className="text-sm">Shortlist candidates from your hiring challenges</p>
                    </td>
                  </tr>
                ) : (
                  filteredCandidates.map((candidate) => (
                    <tr 
                      key={candidate.id} 
                      className={`hover:bg-gray-50 ${selectedCandidates.includes(candidate.id) ? 'bg-purple-50' : ''}`}
                      data-testid={`candidate-row-${candidate.id}`}
                    >
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selectedCandidates.includes(candidate.id)}
                          onChange={() => handleSelectCandidate(candidate.id)}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{candidate.student_details?.full_name || 'N/A'}</p>
                          <p className="text-sm text-gray-500">{candidate.student_details?.email || 'N/A'}</p>
                          <p className="text-xs text-gray-400">{candidate.student_details?.college || ''}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-700">{candidate.challenge_title}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="font-semibold text-purple-600">{candidate.score || 0}</span>
                      </td>
                      <td className="px-4 py-4">
                        <select
                          value={candidate.hiring_status || 'pending'}
                          onChange={(e) => updateCandidateStatus(candidate.id, e.target.value)}
                          className={`px-2 py-1 rounded-lg text-sm font-medium border-0 ${statusColors[candidate.hiring_status || 'pending']}`}
                          data-testid={`status-select-${candidate.id}`}
                        >
                          <option value="pending">Pending</option>
                          <option value="contacted">Contacted</option>
                          <option value="interviewed">Interviewed</option>
                          <option value="offered">Offered</option>
                          <option value="hired">Hired</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {candidate.shortlisted_at ? new Date(candidate.shortlisted_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          {candidate.student_details?.resume_url && (
                            <a
                              href={candidate.student_details.resume_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                              title="View Resume"
                            >
                              <FileText size={16} />
                            </a>
                          )}
                          <button
                            onClick={() => {
                              setSelectedCandidates([candidate.id]);
                              setShowEmailModal(true);
                            }}
                            className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Send Email"
                            data-testid={`email-btn-${candidate.id}`}
                          >
                            <Mail size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Email Modal with Rich Text Editor */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Send size={20} />
                Compose Email
              </h3>
              <button onClick={() => setShowEmailModal(false)} className="text-white/80 hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                <span className="text-purple-700">Recipients selected:</span>
                <span className="font-bold text-purple-700">{selectedCandidates.length} candidate(s)</span>
              </div>
              
              {emailCredits.available_credits < selectedCandidates.length && (
                <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg border border-red-200">
                  <AlertCircle className="text-red-500" size={18} />
                  <span className="text-red-700">
                    Insufficient credits. You need {selectedCandidates.length} but have {emailCredits.available_credits}.
                  </span>
                  <button
                    onClick={() => { setShowEmailModal(false); setShowPurchaseModal(true); }}
                    className="ml-auto text-red-700 underline font-medium"
                  >
                    Buy Credits
                  </button>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., Interview Invitation from Company Name"
                  data-testid="email-subject-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Role (Optional)</label>
                <input
                  type="text"
                  value={jobRole}
                  onChange={(e) => setJobRole(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., Frontend Developer"
                  data-testid="email-job-role-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message * 
                  <span className="text-xs text-gray-500 ml-2">(Supports bold, italic, bullet points, and links)</span>
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden">
                  <ReactQuill
                    theme="snow"
                    value={emailMessage}
                    onChange={setEmailMessage}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Compose your email message here..."
                    className="bg-white"
                    style={{ minHeight: '200px' }}
                  />
                </div>
                <style>{`
                  .ql-container {
                    min-height: 150px;
                    font-size: 14px;
                  }
                  .ql-editor {
                    min-height: 150px;
                  }
                  .ql-toolbar {
                    border-bottom: 1px solid #e5e7eb !important;
                    background: #f9fafb;
                  }
                `}</style>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Interview Instructions (Optional)</label>
                <textarea
                  value={interviewInstructions}
                  onChange={(e) => setInterviewInstructions(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 min-h-[80px]"
                  placeholder="e.g., Date, time, location, video call link..."
                  data-testid="email-instructions-input"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50"
                  data-testid="cancel-email-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendEmail}
                  disabled={sendingEmail || emailCredits.available_credits < selectedCandidates.length}
                  className="flex-1 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-medium hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-send-email-btn"
                >
                  {sendingEmail ? (
                    <><Loader2 className="animate-spin" size={18} /> Sending...</>
                  ) : (
                    <><Send size={18} /> Send {selectedCandidates.length} Email(s)</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Credits Modal */}
      {showPurchaseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-xl">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Package size={20} />
                Purchase Email Credits
              </h3>
              <button onClick={() => setShowPurchaseModal(false)} className="text-white/80 hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                <span className="text-purple-700">Current Balance:</span>
                <span className="font-bold text-purple-700">{emailCredits.available_credits} credits</span>
              </div>
              
              <div className="space-y-3">
                {emailPackages.length > 0 ? (
                  emailPackages.map((pkg) => (
                    <div 
                      key={pkg.id}
                      className="p-4 border-2 border-gray-200 rounded-xl hover:border-purple-400 transition-colors cursor-pointer"
                      onClick={() => !purchaseLoading && handlePurchaseCredits(pkg.id)}
                      data-testid={`package-card-${pkg.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-gray-900">{pkg.name}</h4>
                          <p className="text-sm text-gray-500">{pkg.description}</p>
                          <p className="text-xs text-purple-600 mt-1">Up to {pkg.emails} emails</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-purple-600">₹{pkg.price}</p>
                          <button
                            disabled={purchaseLoading}
                            className="mt-2 px-4 py-1 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
                            data-testid={`buy-package-${pkg.id}`}
                          >
                            {purchaseLoading ? 'Processing...' : 'Buy Now'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    <Package size={32} className="mx-auto mb-2 opacity-50" />
                    <p>No packages available</p>
                    <p className="text-sm">Contact admin to configure pricing</p>
                  </div>
                )}
              </div>
              
              <p className="text-xs text-gray-500 text-center">
                Secure payment via Cashfree. Credits never expire.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Status Update Modal */}
      {showBulkStatusModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-xl">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <RefreshCw size={20} />
                Bulk Update Status
              </h3>
              <button onClick={() => setShowBulkStatusModal(false)} className="text-white/80 hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <span className="text-blue-700">Candidates selected:</span>
                <span className="font-bold text-blue-700">{selectedCandidates.length}</span>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Status</label>
                <select
                  value={bulkStatus}
                  onChange={(e) => setBulkStatus(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-lg"
                  data-testid="bulk-status-select"
                >
                  <option value="pending">Pending</option>
                  <option value="contacted">Contacted</option>
                  <option value="interviewed">Interviewed</option>
                  <option value="offered">Offered</option>
                  <option value="hired">Hired</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowBulkStatusModal(false)}
                  className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkStatusUpdate}
                  disabled={bulkUpdating}
                  className="flex-1 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-medium hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-bulk-status-btn"
                >
                  {bulkUpdating ? (
                    <><Loader2 className="animate-spin" size={18} /> Updating...</>
                  ) : (
                    <><Check size={18} /> Update All</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShortlistedCandidatesPage;
